/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createTheme,
  Mixins,
  Shadows,
  Transitions,
  SxProps,
  Components,
  TransitionsOptions,
  ZIndexOptions,
  TypeText,
} from '@mui/material/styles';
import { ThemeOptions as SystemThemeOptions } from '@mui/system/createTheme';
import {
  Typography,
  TypographyOptions,
} from '@mui/material/styles/createTypography';
import { MixinsOptions } from '@mui/material/styles/createMixins';
import { isRare } from '../util/rarityCheck';
import tinyColor from 'tinycolor2';
import type {} from '@mui/x-data-grid/themeAugmentation';

declare module '@mui/material/styles' {
  interface ThemeOptions extends Omit<SystemThemeOptions, 'zIndex'> {
    mixins?: MixinsOptions;
    components?: Components<Omit<Theme, 'components'>>;
    palette?: PaletteOptions;
    shadows?: Shadows;
    shadowsHard?: typeof SHADOWS_HARD;
    glass?: typeof BACKDROP_FILTERS;
    glow?: typeof GLOWS;
    lineClamp?: typeof LINE_CLAMP;
    panels?: typeof PANELS;
    transitions?: TransitionsOptions;
    typography?: TypographyOptions | ((palette: Palette) => TypographyOptions);
    zIndex?: ZIndexOptions;
    unstable_strictMode?: boolean;
  }

  interface Theme {
    mixins: Mixins;
    palette: typeof PALETTE;
    shadows: Shadows;
    shadowsHard: typeof SHADOWS_HARD;
    glass: typeof BACKDROP_FILTERS;
    glow: typeof GLOWS;
    lineClamp: typeof LINE_CLAMP;
    panels: typeof PANELS;
    transitions: Transitions;
    typography: Typography;
    zIndex: ZIndex;
    unstable_strictMode?: boolean;
  }

  interface ZIndex {
    mobileStepper: number;
    speedDial: number;
    appBar: number;
    drawer: number;
    modal: number;
    snackbar: number;
    tooltip: number;
    fab: number;
    dialog: number;
    glider: number;
  }

  type ZIndexOptions = Partial<ZIndex>;

  interface TypographyVariants {
    buttonLarge: React.CSSProperties;
    buttonLargeSecondary: React.CSSProperties;
    buttonMedium: React.CSSProperties;
    buttonMediumSecondary: React.CSSProperties;
    buttonSmall: React.CSSProperties;
    buttonSmallSecondary: React.CSSProperties;
    inputLabel: React.CSSProperties;
    inputText: React.CSSProperties;
    common: React.CSSProperties;
    standard: React.CSSProperties;
    rare: React.CSSProperties;
    epic: React.CSSProperties;
    legendary: React.CSSProperties;
    helper: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    buttonLarge?: React.CSSProperties;
    buttonLargeSecondary?: React.CSSProperties;
    buttonMedium?: React.CSSProperties;
    buttonMediumSecondary?: React.CSSProperties;
    buttonSmall?: React.CSSProperties;
    buttonSmallSecondary?: React.CSSProperties;
    inputLabel?: React.CSSProperties;
    inputText?: React.CSSProperties;
    common?: React.CSSProperties;
    standard?: React.CSSProperties;
    rare?: React.CSSProperties;
    epic?: React.CSSProperties;
    legendary?: React.CSSProperties;
    helper?: React.CSSProperties;
  }
  interface AvatarVariants {
    gradientOutlined: React.CSSProperties;
  }
  interface AvatarVariantsOptions {
    gradientOutlined?: React.CSSProperties;
  }

  interface AccordionVariants {
    filter: React.CSSProperties;
  }
  interface AccordionVariantsOptions {
    filter?: React.CSSProperties;
  }

  interface ChipVariants {
    gradientOutlined: React.CSSProperties;
    gradientOutlinedHorizontal: React.CSSProperties;
    rarity: React.CSSProperties;
  }
  interface ChipVariantsOptions {
    gradientOutlined?: React.CSSProperties;
    gradientOutlinedHorizontal?: React.CSSProperties;
    rarity?: React.CSSProperties;
  }

  interface CardVariants {
    collection: React.CSSProperties;
    game: React.CSSProperties;
    item: React.CSSProperties;
    itemEmpty: React.CSSProperties;
    profile: React.CSSProperties;
  }
  interface CardVariantsOptions {
    collection?: React.CSSProperties;
    game?: React.CSSProperties;
    item?: React.CSSProperties;
    itemEmpty?: React.CSSProperties;
    profile: React.CSSProperties;
  }

  interface BreakpointOverrides {
    xxl: true;
  }

  type PaletteMode = 'light' | 'dark';

  interface TypeAction {
    active: string;
    mainActive: string;
    hover: string;
    selected: string;
    disabled: string;
    disabledBackground: string;
    focus: string;
    disabledOpaque: string;
    opaque: string;
  }

  interface PaletteColor {
    main: string;
    light: string;
    dark: string;
    mid: string;
    contrastText: string;
    horizontal?: string;
    horizontalInverse?: string;
    vertical?: string;
    verticalInverse?: string;
  }

  type PaletteColorOptions = Partial<PaletteColor>;

  export type PaletteRarity = typeof RARITIES;

  interface TypeBackground {
    light: string;
    dark: string;
    default: string;
    backgroundGradient: (elevation: string) => string;
    elevation: typeof ELEVATIONS;
    elevationSolid: typeof ELEVATIONS_SOLID;
  }

  type PaletteOther = {
    divider: string;
    outlinedBorder: string;
    filledInputBackground: string;
    standardInputLine: string;
    snackbar: string;
    ratingActive: string;
    textSecondaryShades: {
      opacity8: string;
      opacity12: string;
      opacity16: string;
      opacity30: string;
      opacity50: string;
    };
  };

  type Scrollbars = {
    primary: SxProps;
  };

  interface Palette {
    mode: PaletteMode;
    rarity: PaletteRarity;
    rarityHover: PaletteRarity;
    rarityBackground: PaletteRarity;
    text: TypeText;
    disabled: PaletteColor;
    primary: PaletteColor;
    secondary: PaletteColor;
    action: TypeAction;
    error: PaletteColor;
    warning: PaletteColor;
    info: PaletteColor;
    success: PaletteColor;
    background: TypeBackground;
    other: PaletteOther;
    scrollbars: Scrollbars;
  }

  type PaletteOptions = Partial<Palette>;
}

declare module '@mui/material/Badge' {
  interface BadgePropsVariantOverrides {
    online: true;
    onlineLarge: true;
  }
  interface BadgePropsColorOverrides {
    disabled: true;
  }
}

declare module '@mui/material/Avatar' {
  interface AvatarPropsVariantOverrides {
    gradientOutlined: true;
  }
}

declare module '@mui/material/Accordion' {
  interface AccordionPropsVariantOverrides {
    filter: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    // gradientOutlined: true;
    [key: string]: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    buttonLarge: true;
    buttonLargeSecondary: true;
    buttonMedium: true;
    buttonMediumSecondary: true;
    buttonSmall: true;
    buttonSmallSecondary: true;
    common: true;
    standard: true;
    rare: true;
    epic: true;
    legendary: true;
    inputLabel: true;
    inputText: true;
    helper: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    game: true;
    item: true;
    collection: true;
    profile: true;
  }
}

declare module '@mui/material/Divider' {
  interface PaperPropsVariantOverrides {
    text: true;
  }
}

export const SHADOWS: Shadows = [
  'none',
  '0px 0px 1px -1px rgba(57, 156, 250, 0.4), 0px 0px 1px rgba(57, 156, 250, 0.28), 0px 0px 3px rgba(57, 156, 250, 0.24)',
  '0px 0px 1px -2px rgba(57, 156, 250, 0.4), 0px 0px 2px rgba(57, 156, 250, 0.28), 0px 0px 5px rgba(57, 156, 250, 0.24)',
  '0px 0px 3px -2px rgba(57, 156, 250, 0.4), 0px 0px 4px rgba(57, 156, 250, 0.28), 0px 0px 8px rgba(57, 156, 250, 0.24)',
  '0px 0px 4px -1px rgba(57, 156, 250, 0.4), 0px 0px 5px rgba(57, 156, 250, 0.28), 0px 0px 10px rgba(0, 51, 233, 0.24)',
  '0px 0px 5px -1px rgba(57, 156, 250, 0.4), 0px 0px 8px rgba(57, 156, 250, 0.28), 0px 0px 14px rgba(57, 156, 250, 0.24)',
  '0px 0px 5px -1px rgba(57, 156, 250, 0.4), 0px 0px 10px rgba(57, 156, 250, 0.28), 0px 0px 16px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 5px -3px rgba(57, 156, 250, 0.4), 0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 14px 2px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 6px -3px rgba(57, 156, 250, 0.4), 0px 0px 12px 1px rgba(57, 156, 250, 0.28), 0px 0px 16px 2px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 6px -3px rgba(57, 156, 250, 0.4), 0px 0px 14px 1px rgba(57, 156, 250, 0.28), 0px 0px 16px 3px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 7px -4px rgba(57, 156, 250, 0.4), 0px 0px 15px 1px rgba(57, 156, 250, 0.28), 0px 0px 20px 3px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 8px -4px rgba(57, 156, 250, 0.4), 0px 0px 17px 2px rgba(57, 156, 250, 0.28), 0px 0px 22px 4px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 8px -4px rgba(57, 156, 250, 0.4), 0px 0px 19px 2px rgba(57, 156, 250, 0.28), 0px 0px 24px 4px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 9px -4px rgba(57, 156, 250, 0.4), 0px 0px 21px 2px rgba(57, 156, 250, 0.28), 0px 0px 26px 4px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 9px -5px rgba(57, 156, 250, 0.4), 0px 0px 22px 2px rgba(57, 156, 250, 0.28), 0px 0px 28px 5px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 10px -5px rgba(57, 156, 250, 0.4), 0px 0px 24px 2px rgba(57, 156, 250, 0.28), 0px 0px 30px 5px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 11px -5px rgba(57, 156, 250, 0.4), 0px 0px 26px 2px rgba(57, 156, 250, 0.28), 0px 0px 32px 5px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 11px -5px rgba(57, 156, 250, 0.4), 0px 0px 28px 2px rgba(57, 156, 250, 0.28), 0px 0px 34px 6px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 12px -6px rgba(57, 156, 250, 0.4), 0px 0px 29px 2px rgba(57, 156, 250, 0.28), 0px 0px 36px 6px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 13px -6px rgba(57, 156, 250, 0.4), 0px 0px 31px 3px rgba(57, 156, 250, 0.28), 0px 0px 38px 7px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 13px -6px rgba(57, 156, 250, 0.4), 0px 0px 33px 3px rgba(57, 156, 250, 0.28), 0px 0px 40px 7px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 14px -6px rgba(57, 156, 250, 0.4), 0px 0px 35px 3px rgba(57, 156, 250, 0.28), 0px 0px 42px 7px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 14px -7px rgba(57, 156, 250, 0.4), 0px 0px 36px 3px rgba(57, 156, 250, 0.28), 0px 0px 44px 8px rgba(57, 156, 250, 0.24)',
  '0px 0px 10px 1px rgba(57, 156, 250, 0.28), 0px 0px 5px -2px rgba(57, 156, 250, 0.4), 0px 0px 16px 1px rgba(57, 156, 250, 0.24)', // '0px 0px 15px -7px rgba(57, 156, 250, 0.4), 0px 0px 38px 3px rgba(57, 156, 250, 0.28), 0px 0px 46px 8px rgba(57, 156, 250, 0.24)',
];

export const SHADOWS_HARD_COLOR = {
  primary: 'rgba(7, 50, 201, 0.8)',
  secondary: 'rgba(13, 78, 106, 0.8)',
  error: 'rgba(165, 9, 9, 0.6)',
  warning: 'rgba(255, 100, 13, 0.8)',
  info: 'rgba(7, 50, 201, 0.8)',
  success: 'rgba(13, 78, 106, 0.8)',
  royal: 'rgba(112, 54, 178, 0.8)',
};

export const SHADOWS_HARD_DROPSHADOW = ['0.5px 1.5px 0.5px', '0px 2px 0.5px'];

type ShadowsHard = {
  [key in
    | `${
        | 'primary'
        | 'secondary'
        | 'error'
        | 'warning'
        | 'info'
        | 'success'
        | 'royal'}Elevation${1 | 2}`
    | 'emboss']: string;
};

export const SHADOWS_HARD: ShadowsHard = {
  ...(Object.keys(SHADOWS_HARD_COLOR).reduce((prev, color) => {
    SHADOWS_HARD_DROPSHADOW.forEach((shadow, i) => {
      prev[`${color}Elevation${i + 1}`] = `${shadow} ${
        SHADOWS_HARD_COLOR[`${color}`]
      }`;
    });
    return prev;
  }, {}) as Omit<ShadowsHard, 'emboss'>),
  emboss:
    '-1px -1px 1px 0px rgba(0, 100, 233, 0.9), 1px 1px 0.5px rgba(2, 40, 174, 0.5), 0px 0px 5px rgba(57, 156, 250, 0.12)',
};

export const BACKDROP_FILTERS: Shadows = [
  'none',
  'blur(4px)',
  'blur(5px)',
  'blur(6px)',
  'blur(7px)',
  'blur(8px)',
  'blur(9px)',
  'blur(10px)',
  'blur(11px)',
  'blur(12px)',
  'blur(13px)',
  'blur(14px)',
  'blur(15px)',
  'blur(16px)',
  'blur(17px)',
  'blur(18px)',
  'blur(19px)',
  'blur(20px)',
  'blur(21px)',
  'blur(22px)',
  'blur(23px)',
  'blur(24px)',
  'blur(25px)',
  'blur(26px)',
  'blur(27px)',
];

export const GLOWS = {
  card: {
    '&:hover': {
      cursor: 'pointer',
      transition: 'all 0.01s ease-in-out',
      boxShadow:
        '0px 0px 18.444px rgba(57, 156, 250, 0.7), 0px 0px 13.968px rgba(57, 156, 250, 0.7), 0px 0px 8.148px rgba(57, 156, 250, 0.7), 0px 0px 4.074px rgba(57, 156, 250, 0.7), 0px 0px 1.164px rgba(57, 156, 250, 0.7), 0px 0px 0.582px rgba(57, 156, 250, 0.7)',
    },
  },
  panel: {
    boxShadow: SHADOWS[4],
    '&:hover': {
      boxShadow: SHADOWS[7],
    },
  },
  dialog: {
    boxShadow: SHADOWS[4], // SHADWOWS[8]
    '&:hover': {
      boxShadow: SHADOWS[7],
    },
  },
  highlight: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      background: '#4DC8FF',
      opacity: 0,
      transition: '0.3s ease',
    },
    '&:hover:before': {
      opacity: 0.05,
    },
  },
};

export const ELEVATIONS = {
  '0': 'linear-gradient(180deg, #000E21 0%, #001533 100%)',
  '1': 'rgba(0, 14, 33, 0.8)',
  '2': 'rgba(0, 14, 33, 0.8)',
  '3': 'rgba(0, 16, 38, 0.8)',
  '4': 'rgba(0, 19, 43, 0.8)',
  '5': 'rgba(0, 19, 43, 0.8)',
  '6': 'rgba(0, 22, 48, 0.8)',
  '7': 'rgba(0, 24, 54, 0.8)',
  '8': 'rgba(0, 27, 59, 0.8)',
  '9': 'rgba(1, 30, 64, 0.8)',
  '10': 'rgba(1, 34, 69, 0.8)',
  '11': 'rgba(2, 37, 74, 0.8)',
  '12': 'rgba(3, 41, 79, 0.8)',
  '13': 'rgba(4, 44, 84, 0.8)',
  '14': 'rgba(5, 49, 89, 0.8)',
  '15': 'rgba(6, 51, 92, 0.8)',
  '16': 'rgba(7, 47, 84, 0.8)',
  '17': 'rgba(9, 54, 97, 0.8)',
  '18': 'rgba(10, 56, 99, 0.8)',
  '19': 'rgba(10, 59, 102, 0.8)',
  '20': 'rgba(10, 61, 105, 0.8)',
  '21': 'rgba(11, 64, 107, 0.8)',
  '22': 'rgba(11, 65, 110, 0.8)',
  '23': 'rgba(11, 68, 112, 0.8)',
  '24': 'rgba(11, 70, 115, 0.8)',
};

export const ELEVATIONS_SOLID = {
  '0': 'linear-gradient(180deg, #000E21 0%, #001533 100%)',
  '1': 'rgba(0, 14, 33, 1)',
  '2': 'rgba(0, 14, 33, 1)',
  '3': 'rgba(0, 16, 38, 1)',
  '4': 'rgba(0, 19, 43, 1)',
  '5': 'rgba(0, 19, 43, 1)',
  '6': 'rgba(0, 22, 48, 1)',
  '7': 'rgba(0, 24, 54, 1)',
  '8': 'rgba(0, 27, 59, 1)',
  '9': 'rgba(1, 30, 64, 1)',
  '10': 'rgba(1, 34, 69, 1)',
  '11': 'rgba(2, 37, 74, 1)',
  '12': 'rgba(3, 41, 79, 1)',
  '13': 'rgba(4, 44, 84, 1)',
  '14': 'rgba(5, 49, 89, 1)',
  '15': 'rgba(6, 51, 92, 1)',
  '16': 'rgba(7, 47, 84, 1)',
  '17': 'rgba(9, 54, 97, 1)',
  '18': 'rgba(10, 56, 99, 1)',
  '19': 'rgba(10, 59, 102, 1)',
  '20': 'rgba(10, 61, 105, 1)',
  '21': 'rgba(11, 64, 107, 1)',
  '22': 'rgba(11, 65, 110, 1)',
  '23': 'rgba(11, 68, 112, 1)',
  '24': 'rgba(11, 70, 115, 1)',
};

export const RARITIES = {
  common: 'rgba(204, 226, 255, 0.7)',
  standard: 'rgba(204, 226, 255, 0.7)',
  rare: '#0097A7',
  epic: '#D500F9',
  legendary: '#FFCA28',
  gold: '#F1C305',
  iridescent: '#B367F5',
  basic: '#4DC8FF',
  trace: 'rgba(255,143,0,1)',
  safari: 'rgba(144,114,47,1)',
  caution: 'rgba(192,46,36,1)',
  corsair: 'rgba(228,172,41,1)',
  ice: 'rgba(118,232,255,1)',
  puncher: 'rgba(34,80,110,1)',
  diy: 'rgba(255,182,115,1)',
  toxic: 'rgba(220,69,227,1)',
  strike: 'rgba(250,60,40,1)',
  lava: 'rgba(233,67,38,1)',
  flames: 'rgba(222,98,60,1)',
  justice: 'rgba(194,48,45,1)',
  cards: 'rgba(200,61,61,1)',
  kitty: 'rgba(255,124,180,1)',
  'old glory': 'rgba(7,21,76,1)',
  neon: 'rgba(128,252,252,1)',
  anodized: 'rgba(61,139,135,1)',
  camouflage: 'rgba(171,179,230,1)',
  alert: 'rgba(82,171,181,1)',
  pursuit: 'rgba(161,51,56,1)',
  skull: 'rgba(26,26,44,1)',
  rhyno: 'rgba(0, 255, 150, 1)',
  vandal: 'rgba(16,189,255,1)',
  cleaner: 'rgba(170, 169, 173, 1)',
  explode: 'rgba(255, 215, 0, 1)',
  celt: 'rgba(192, 192, 192, 1)',
  holiday: 'rgba(229, 48, 57, 1)',
  flow: 'rgba(255, 0, 255, 1)',
  chopper: 'rgba(186, 140, 99, 1)',
  memories: 'rgba(0, 105, 148, 1)',
  primal: 'rgba(66, 28, 1, 1)',
  mooncrash: 'rgba(255, 165, 0, 1)',
  steampunk: 'rgba(27, 163, 156, 1)',
  myth: 'rgba(187, 194, 204, 1)',
  octopus: 'rgba(242, 189, 215, 1)',
  hunt: 'rgba(128, 0, 0, 1)',
  desert: 'rgba(177, 166, 136, 1)',
  'metaderby-pacer': 'rgba(204, 226, 255, 0.7)',
  'metaderby-rare': '#14fa83',
  'metaderby-elite': '#ff315e',
  'metaderby-epic': '#B367F5',
  'metaderby-legendary': '#F1C305',
};

export const RARITIES_HOVER = {
  common: 'rgba(204, 226, 255, 0.5)',
  standard: 'rgba(204, 226, 255, 0.5)',
  rare: 'rgba(0, 151, 167, 0.5)',
  epic: 'rgba(213, 0, 249, 0.5)',
  legendary: 'rgba(255, 202, 40, 0.5)',
  gold: 'rgba(241, 195, 5, 0.5)',
  iridescent: 'rgba(179, 103, 245, 0.5)',
  trace: 'rgba(255,143,0,0.5)',
  safari: 'rgba(144,114,47,0.5)',
  caution: 'rgba(192,46,36,0.5)',
  corsair: 'rgba(228,172,41,0.5)',
  ice: 'rgba(118,232,255,0.5)',
  puncher: 'rgba(34,80,110,0.5)',
  diy: 'rgba(255,182,115,0.5)',
  toxic: 'rgba(220,69,227,0.5)',
  strike: 'rgba(250,60,40,0.5)',
  lava: 'rgba(233,67,38,0.5)',
  flames: 'rgba(222,98,60,0.5)',
  justice: 'rgba(194,48,45,0.5)',
  cards: 'rgba(200,61,61,0.5)',
  kitty: 'rgba(183,89,130,0.5)',
  'old glory': 'rgba(7,21,76,0.5)',
  neon: 'rgba(128,252,252,0.5)',
  anodized: 'rgba(61,139,135,0.5)',
  camouflage: 'rgba(171,179,230,0.5)',
  alert: 'rgba(82,171,181,0.5)',
  pursuit: 'rgba(255,80,91,1)',
  skull: 'rgba(26,26,44,0.5)',
  rhyno: 'rgba(0, 255, 150, 0.5)',
  vandal: 'rgba(16,189,255,1)',
  cleaner: 'rgba(170, 169, 173, 0.5)',
  explode: 'rgba(255, 215, 0, 0.5)',
  celt: 'rgba(192, 192, 192, 0.5)',
  holiday: 'rgba(229, 48, 57, 0.5)',
  flow: 'rgba(255, 0, 255, 0.5)',
  chopper: 'rgba(186, 140, 99, 0.5)',
  memories: 'rgba(0, 105, 148, 0.5)',
  primal: 'rgba(66, 28, 1, 0.5)',
  mooncrash: 'rgba(255, 165, 0, 0.5)',
  steampunk: 'rgba(27, 163, 156, 0.5',
  myth: 'rgba(187, 194, 204, 0.5)',
  octopus: 'rgba(242, 189, 215, 0.5)',
  hunt: 'rgba(128, 0, 0, 0.5)',
  desert: 'rgba(177, 166, 136, 0.5)',
  'metaderby-pacer': 'rgba(204, 226, 255, 0.5)',
  'metaderby-rare': 'rgba(20, 250, 131, 0.5)',
  'metaderby-elite': 'rgba(255, 49, 94, 0.5)',
  'metaderby-epic': 'rgba(179, 103, 245, 0.5)',
  'metaderby-legendary': 'rgba(241, 195, 5, 0.5)',
};

export const RARITIES_BACKGROUND = {
  disabled:
    'linear-gradient(0deg, rgba(91, 101, 113, 0.5) 0%, rgba(142, 152, 164, 0.4) 90%)',
  epic: 'linear-gradient(180deg, #B86DD2 0%, #7630d1 100%)',
  legendary: 'linear-gradient(180deg, #FFCB44 -13.1%, #FA9808 100%)',
  rare: 'linear-gradient(180deg, #15DBCF 0%, #047150 255.95%), linear-gradient(0deg, #FFFFFF, #FFFFFF)',
  common: 'linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
  standard: 'linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
  gold: 'linear-gradient(180deg, #F1C305 0%, #D1840E 100%)',
  iridescent: 'linear-gradient(180deg, #F9B6CA 0%, #B367F5 100%)',
  basic: 'linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
  trace: 'linear-gradient(90deg, #ff8f00 0%, #53bade 100%)',
  safari: 'linear-gradient(90deg, #90722f 0%, #3a3436 100%)',
  caution: 'linear-gradient(90deg, #c02e24 0%, #f2b647 100%)',
  corsair: 'linear-gradient(90deg, #e4ac29 0%, #414141 100%)',
  ice: 'linear-gradient(90deg, #76e8ff 0%, #5aa1ff 100%)',
  puncher: 'linear-gradient(90deg, #22506e 0%, #cdaf56 100%)',
  diy: 'linear-gradient(90deg, #ffb673 0%, #57b7ff 100%)',
  toxic: 'linear-gradient(90deg, #dc45e3 0%, #79ee4b 100%)',
  strike: 'linear-gradient(90deg, #fa3c28 0%, rgba(170,170,170,1) 100%)',
  lava: 'linear-gradient(90deg, rgba(233,67,38,1) 0%, #aaaaaa 100%)',
  flames: 'linear-gradient(90deg, #de623c 0%, #f8dc59 100%)',
  justice: 'linear-gradient(90deg, #c2302d 0%, #131c23 100%)',
  cards: 'linear-gradient(90deg, #c83d3d 0%, #dce3a5 100%)',
  kitty: 'linear-gradient(90deg, #ff7cb4 0%, #d199ff 100%)',
  'old glory': 'linear-gradient(90deg, #07154c 0%, #ffffff 50%, #b02a12 100%)',
  neon: 'linear-gradient(90deg, #80fcfc 0%, #3babff 100%)',
  anodized: 'linear-gradient(90deg, #3d8b87 0%, #272524 100%)',
  camouflage: 'linear-gradient(90deg, #bcc4ff 0%, #6c7d7b 50%, #4c54b4 100%)',
  alert: 'linear-gradient(90deg, #52abb5 0%, #86449e 100%)',
  pursuit: 'linear-gradient(90deg, #ff505b 0%, #a0aab4 100%)',
  skull: 'linear-gradient(90deg, #1a1a2c 0%, #c23f68 100%)',
  rhyno: 'linear-gradient(90deg, #00ff96 0%, #ffffff 100%)',
  vandal: 'linear-gradient(90deg, #10bdff 0%, #8768b0 100%)',
  cleaner: 'linear-gradient(90deg,#aaa9ae 0%, #505c60 100%)',
  explode: 'linear-gradient(90deg,#ffd700 0%, #622f22 100%)',
  celt: 'linear-gradient(90deg,#c0c0c0 0%, #a47449 100%)',
  holiday: 'linear-gradient(90deg,#ffffff 0%, #ffc0cb 50%,#e53039 100%)',
  flow: 'linear-gradient(90deg, #4b0082 0%, #ff00ff 100%)',
  chopper: 'linear-gradient(90deg, #808080 0%, #ba8c63 100%)',
  memories: 'linear-gradient(90deg, #fff000 0%, #006994 100%)',
  primal: 'linear-gradient(90deg, #b5651d 0%, #421c01 100%)',
  mooncrash: 'linear-gradient(90deg, #696969 0%, #ffa500 100%)',
  steampunk: 'linear-gradient(90deg, #c8640a 0%, #1ba39c 100%)',
  myth: 'linear-gradient(90deg, #080808 0%, #bbc2cc 100%)',
  octopus: 'linear-gradient(90deg, #444c38 0%, #f2bdd7 100%)',
  hunt: 'linear-gradient(90deg, #696969 0%,#800000 100%)',
  desert: 'linear-gradient(90deg, #eaddca 0%, #b1a688 100%)',
  'metaderby-pacer': 'linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
  'metaderby-rare': 'linear-gradient(180deg, #0cc97e 0%, #14fa83 100%)',
  'metaderby-elite': 'linear-gradient(180deg, #A31232 0%, #FF315E 100%)',
  'metaderby-epic': 'linear-gradient(180deg, #8332C9 0%, #B367F5 100%)',
  'metaderby-legendary': 'linear-gradient(180deg, #F1C305 0%, #D1840E 100%)',
};

export const PALETTE = {
  // mode: 'dark',
  rarity: RARITIES,
  rarityHover: RARITIES_HOVER,
  rarityBackground: RARITIES_BACKGROUND,
  primary: {
    main: '#4DC8FF',
    light: '#4DC8FF',
    dark: '#0064E9',
    mid: '#2796F4',
    horizontal: 'linear-gradient(90deg, #4DC8FF -33%, #0064E9 200%)',
    horizontalInverse: 'linear-gradient(90deg, #0064E9 -33%, #4DC8FF 200%)',
    vertical: 'linear-gradient(0deg, #0064E9 -33%, #4DC8FF 90%)',
    verticalInverse: 'linear-gradient(180deg, #0064E9 16.67%, #4DC8FF 200%)',
    horizontalLight: 'linear-gradient(90deg, #2EA0F6 0%, #4DC8FF 200%)',
    contrastText: '#fff',
  },
  secondary: {
    main: '#15DBCF',
    light: '#15DBCF',
    dark: '#0DA690',
    mid: '#11C1B0',
    horizontal: 'linear-gradient(90deg, #15DBCF 0%, #0DA690 100%)',
    horizontalInverse: 'linear-gradient(270deg, #15DBCF 0%, #0DA690 100%)',
    vertical: 'linear-gradient(0deg, #0DA690 0%, #15DBCF 90%)',
    verticalInverse: 'linear-gradient(180deg, #0DA690 16.67%, #15DBCF 100%)',
    contrastText: '#fff',
  },
  error: {
    main: '#FE512B',
    light: '#FE512B',
    dark: '#b22222',
    mid: '#D83A27',
    horizontal: 'linear-gradient(90deg, #FE512B 0%, #b22222 200%)',
    horizontalInverse: 'linear-gradient(90deg, #b22222 0%, #FE512B 200%)',
    vertical: 'linear-gradient(0deg, #b22222 -25%, #FE512B 90%)',
    verticalInverse: 'linear-gradient(180deg, #b22222 16.67%, #FE512B 200%)',
    contrastText: '#fff',
  },
  warning: {
    main: '#FFAB31',
    light: '#FFAB31',
    dark: '#ED7103',
    mid: '#F68E1A',
    horizontal: 'linear-gradient(90deg, #FFAB31 -25%, #ED7103 115%)',
    horizontalInverse: 'linear-gradient(90deg, #ED7103 -25%, #FFAB31 115%)',
    vertical: 'linear-gradient(0deg, #ED7103 -25%, #FFAB31 90%)',
    verticalInverse: 'linear-gradient(180deg, #ED7103 16.67%, #FFAB31 200%)',
    contrastText: '#fff',
  },
  info: {
    // Same as primary
    main: '#4DC8FF',
    light: '#4DC8FF',
    dark: '#0064E9',
    mid: '#2796F4',
    horizontal: 'linear-gradient(90deg, #4DC8FF -33%, #0064E9 200%)',
    horizontalInverse: 'linear-gradient(90deg, #0064E9 -33%, #4DC8FF 200%)',
    vertical: 'linear-gradient(0deg, #0064E9 -33%, #4DC8FF 90%)',
    verticalInverse: 'linear-gradient(180deg, #0064E9 16.67%, #4DC8FF 200%)',
    contrastText: '#fff',
  },
  success: {
    main: '#15DBCF',
    light: '#15DBCF',
    dark: '#0DA690',
    mid: '#11C1B0',
    horizontal: 'linear-gradient(90deg, #15DBCF 0%, #0DA690 100%)',
    horizontalInverse: 'linear-gradient(270deg, #15DBCF 0%, #0DA690 100%)',
    vertical: 'linear-gradient(0deg, #0DA690 0%, #15DBCF 90%)',
    verticalInverse: 'linear-gradient(180deg, #0DA690 16.67%, #15DBCF 100%)',
    contrastText: '#fff',
  },
  royal: {
    main: '#C999FF',
    light: '#C999FF',
    dark: '#661AFF',
    mid: '#8E4DFF',
    horizontal: 'linear-gradient(90deg, #C999FF -33%, #661AFF 200%)',
    horizontalInverse: 'linear-gradient(90deg, #661AFF -33%, #C999FF 200%)',
    vertical: 'linear-gradient(0deg, #661AFF -33%, #C999FF 90%)',
    verticalInverse: 'linear-gradient(180deg, #661AFF 16.67%, #C999FF 200%)',
  },
  // primary: {
  //   main: '#4DC8FF',
  //   light: '#4DC8FF',
  //   dark: '#0064E9',
  //   mid: '#2796F4',
  //   horizontal: 'linear-gradient(90deg, #4DC8FF -33%, #0064E9 200%)',
  //   horizontalInverse: 'linear-gradient(90deg, #0064E9 -33%, #4DC8FF 200%)',
  //   vertical: 'linear-gradient(0deg, #0064E9 -33%, #4DC8FF 90%)',
  //   verticalInverse: 'linear-gradient(180deg, #0064E9 16.67%, #4DC8FF 200%)',
  //   contrastText: '#fff',
  // },
  disabled: {
    main: 'rgba(102, 113, 126, 1)',
    vertical: 'linear-gradient(0deg, #66717E 0%, #50525A 90%)',
  },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(204, 226, 255, 0.7)',
    disabled: 'rgba(204, 227, 255, 0.5)',
  },
  background: {
    light: '#001533',
    dark: '#000E21',
    // default: 'linear-gradient(180deg, #000E21 0%, #001533 100%)',
    elevation: ELEVATIONS,
    elevationSolid: ELEVATIONS_SOLID,
    backgroundGradient: (elevation: string) => {
      return `linear-gradient(${ELEVATIONS_SOLID[`${elevation}`]}, ${
        ELEVATIONS_SOLID[`${elevation}`]
      }),
        'linear-gradient(0deg, #0064E9 -33%, #4DC8FF 90%)'`;
    },
  },
  action: {
    active: '#4DC6FF',
    mainActive: 'linear-gradient(90deg, #4DC6FF 0%, #0065E8 177.78%)',
    hover: 'rgba(128, 183, 255, 0.08)',
    selected: 'rgba(128, 183, 255, 0.16)',
    disabled: 'rgba(128, 183, 255, 0.3)',
    disabledBackground: 'rgba(0, 111, 255, 0.12)',
    focus: 'rgba(128, 183, 255, 0.12)',
    disabledOpaque: '#2D4059',
    opaque: '#80B7FF',
  },
  other: {
    divider: 'linear-gradient(0deg, #0064E9 16.67%, #4DC8FF 200%)', // Same as primary/vertical
    outlinedBorder: 'linear-gradient(0deg, #0064E9 16.67%, #4DC8FF 200%)', // Same as primary/vertical
    filledInputBackground: 'rgba(0, 101, 232, 0.14)',
    standardInputLine:
      'linear-gradient(90deg, rgba(77, 198, 255, 0.7) 16.67%, rgba(0, 101, 232, 0.7) 115%)',
    snackbar: 'rgba(11, 70, 115, 0.8)', // Same as background/elevation.24
    ratingActive: 'linear-gradient(180deg, #FFF64D 0%, #E8A400 100%)',
    textSecondaryShades: {
      opacity8: 'rgba(125, 178, 249, 0.08)',
      opacity12: 'rgba(125, 178, 249, 0.12)',
      opacity16: 'rgba(125, 178, 249, 0.16)',
      opacity30: 'rgba(125, 178, 249, 0.30)',
      opacity50: 'rgba(125, 178, 249, 0.50)',
    },
  },
  scrollbars: {
    primary: {
      '::-webkit-scrollbar': {
        width: '16px',
      },
      '::-webkit-scrollbar-track': {
        background: '#19293e',
        borderRadius: '10px',
        // This border + background-clip achieves the effect of the track being thinner than the thumb
        backgroundClip: 'padding-box',
        border: '6px solid transparent',
      },
      '::-webkit-scrollbar-thumb': {
        background: 'linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
        backgroundClip: 'padding-box',
        border: '4px solid transparent',
        borderRadius: '10px',
      },
      '::-webkit-scrollbar-track-piece:end': {
        background: 'transparent',
        marginBottom: '4px',
      },
      '::-webkit-scrollbar-track-piece:start': {
        background: 'transparent',
        marginTop: '4px',
      },
      '::-webkit-scrollbar-corner': {
        background: 'transparent',
      },
    },
  },
};
const TEXT_DECORATION = {
  textUnderlineOffset: '2px',
  textDecorationThickness: '1px',
};
const TYPOGRAPHY: TypographyOptions = {
  htmlFontSize: 14,
  fontFamily: 'Barlow',
  h1: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '96px',
    lineHeight: 1.167,
    letterSpacing: '-1.5px',
  },
  h2: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '60px',
    lineHeight: 1.2,
    letterSpacing: '-0.5px',
  },
  h3: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '40px',
    lineHeight: 1.167,
  },
  h4: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '32px',
    lineHeight: 1.235,
    letterSpacing: '0.25px',
  },
  h5: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: 1.334,
  },
  h6: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: 1.6,
    letterSpacing: '0.15px',
  },
  body1: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: 1.5,
    letterSpacing: '0.15px',
  },
  body2: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: 1.43,
    letterSpacing: '0.17px',
  },
  subtitle1: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: 1.75,
    letterSpacing: '0.15px',
  },
  subtitle2: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: 1.57,
    letterSpacing: '0.1px',
  },
  overline: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 2.66,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },
  caption: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: 1.66,
    letterSpacing: '0.4px',
  },
  button: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1.24,
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
  },
  buttonLarge: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '26px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.primary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  buttonLargeSecondary: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '26px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.secondary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  buttonMedium: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.primary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  buttonMediumSecondary: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.secondary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  buttonSmall: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '22px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.primary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  buttonSmallSecondary: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '22px',
    letterSpacing: '0.46px',
    textTransform: 'uppercase',
    background: PALETTE.secondary.horizontal,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  common: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: PALETTE.rarity.common,
  },
  standard: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: PALETTE.rarity.standard,
  },
  rare: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: PALETTE.rarity.rare,
  },
  epic: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: PALETTE.rarity.epic,
  },
  legendary: {
    ...TEXT_DECORATION,
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.17px',
    color: PALETTE.rarity.legendary,
  },
  inputLabel: {
    fontFamily: 'Barlow',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: ' 0.15px',
    color: 'white',
  },
  helper: {
    fontFamily: 'Barlow',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '0.4px',
    color: 'rgba(204, 226, 255, 0.7)',
  },
};

const BREAKPOINTS = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1500,
    xxl: 1800,
  },
};

const ZINDEX: ZIndexOptions = {
  mobileStepper: 1000,
  speedDial: 1050,
  appBar: 1600,
  drawer: 1100,
  modal: Number.MAX_SAFE_INTEGER - 10,
  dialog: Number.MAX_SAFE_INTEGER - 10,
  snackbar: 1400,
  tooltip: 900,
  glider: 800,
};

const LINE_CLAMP = (
  lines: number,
  options = { wrap: true, textOverflow: 'ellipsis' },
) => {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: options?.textOverflow,
    whiteSpace: options?.wrap ? ('normal' as const) : ('nowrap' as const),
  };
};

const PANELS = [
  {
    background: ELEVATIONS['8'],
    borderRadius: '10px',
    p: 4,
  },
  {
    background: ELEVATIONS['11'],
    borderRadius: '10px',
    p: '12px',
  },
  {
    backkground: ELEVATIONS['14'],
    borderRadius: '5px',
    p: '12px',
  },
  {
    //panel list
    borderRadius: '10px',
    position: 'relative',
    margin: 'auto',
    backgroundImage: `linear-gradient(rgba(13,31,50,1), rgba(13,31,50,1)), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
    border: 'double 2px transparent',
    backgroundClip: 'content-box, border-box',
    backgroundOrigin: 'border-box',
    backdropFilter: BACKDROP_FILTERS[4],
    ...GLOWS.panel,
  },
  {
    //panel list item
    position: 'relative',
    borderRadius: '10px',
    p: '24px 16px 0 16px',
    '&::after': {
      content: "''",
      inset: -2,
      position: 'absolute',
      borderRadius: '10px',
      border: 'solid 2px transparent',
      backgroundImage:
        'linear-gradient(#000E21, #001533), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'content-box, border-box',
      zIndex: -1,
    },
  },
] as const;

const theme = createTheme({
  palette: PALETTE,
  spacing: 4,
  typography: TYPOGRAPHY,
  breakpoints: BREAKPOINTS,
  shadows: SHADOWS,
  shadowsHard: SHADOWS_HARD,
  glass: BACKDROP_FILTERS,
  glow: GLOWS,
  zIndex: ZINDEX,
  lineClamp: LINE_CLAMP,
  panels: PANELS,
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          border: 'solid 2px transparent',
          backgroundImage: `linear-gradient(${PALETTE.background.elevationSolid[2]}, 
        ${PALETTE.background.elevationSolid[2]}), 
        ${PALETTE.primary.vertical}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: SHADOWS[4],
          '&:hover': {
            boxShadow: SHADOWS[7],
          },
        },
        columnHeaders: {
          borderRadius: '10px 10px 0px 0px',
          borderBottom: 'solid 2px transparent',
          backgroundImage: `linear-gradient(${PALETTE.background.elevationSolid[10]}, 
          ${PALETTE.background.elevationSolid[10]}), 
          ${PALETTE.primary.horizontal}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          boxShadow: SHADOWS[7],
        },
        cell: {
          borderColor: '#ffffff25',
          '&:focus': {
            outline: 'none',
          },
        },
        row: {
          borderColor: '#ffffff25',
          '&:focus': {
            outline: 'none',
          },
          '&:hover': {
            boxShadow: SHADOWS[7],
          },
        },
        columnSeparator: {
          display: 'none',
        },
        footerContainer: {
          border: 'unset',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: SHADOWS[4],
          backdropFilter: BACKDROP_FILTERS[4],
          borderBottom: `1px solid ${PALETTE.primary.dark}`,
          backgroundColor: PALETTE.background.elevation['4'],
          height: '60px',
        },
      },
    },
    MuiAvatar: {
      variants: [
        {
          props: { variant: 'gradientOutlined' },
          style: {
            border: 'double 1.5px transparent',
            borderRadius: '50%',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: SHADOWS_HARD.primaryElevation1,
          },
        },
      ],
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'collection' },
          style: {
            width: '250px',
            height: '330px',
            border: 'double 2px transparent',
            '& .MuiCardContent-root': {
              position: 'relative',
              display: 'flex',
              height: '85.5%',
              alignItems: 'flex-end',
              zIndex: 1,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 8,
              paddingRight: 8,
            },
            '& .MuiCardActions-root': {
              position: 'relative',
              zIndex: 2,
              paddingLeft: 4,
              paddingRight: 4,
              paddingTop: 4,
              paddingBottom: 0,
            },
            '& .MuiCardMedia-root': {
              width: '247px',
              height: '323px',
              position: 'absolute',
              borderRadius: '10px',
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              backgroundPosition: 'center top',
              display: 'block',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              '&:after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                width: '100%',
                height: '20%',
                bottom: 0,
                zIndex: 1,
                background: 'linear-gradient(to top, #000, rgba(0,0,0,0))',
              },
            },
          },
        },
        {
          props: { variant: 'game' },
          style: {
            width: '534px',
            '& .MuiCardMedia-root': {
              borderRadius: '5px',
            },
            '& .MuiCardActions-root': {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'stretch',
              paddingTop: 8,
              paddingBottom: 8,
              paddingLeft: 16,
              paddingRight: 16,
            },
          },
        },
        {
          props: { variant: 'profile' },
          style: {
            width: '240px',
            maxHeight: '140px',
          },
        },
        {
          props: { variant: 'item' },
          style: {
            border: 'double 2px transparent',
            width: '254px',
            '& .MuiCardMedia-root': {
              borderRadius: '5px',
              height: '248px',
              backgroundPosition: 'center top',
            },
            '& .MuiCardContent-root:last-child': {
              paddingBottom: 2,
            },
            '& .MuiCardContent-root': {
              padding: 8,
              paddingBottom: 0,
              paddingTop: 0,
            },
            '& .MuiCardActions-root': {
              padding: 4,
            },
          },
        },
        {
          props: { variant: 'itemEmpty' },
          style: {
            border: 'dashed 2px #051D33',
            minWidth: '254px',
            maxWidth: '254px',
            '& .MuiCardMedia-root': {
              height: '248px',
              backgroundPosition: 'center top',
              borderRadius: '10px',
              backgroundOrigin: 'border-box',
              backgroundClip: 'content-box, border-box',
              backgroundImage: 'linear-gradient(#051D33, #051D33), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)',
              '&:hover': {
                background: 'none',
                cursor: 'auto',
              }
            },
            '& .MuiCardContent-root:last-child': {
              paddingBottom: 2,
            },
            '& .MuiCardContent-root': {
              padding: 8,
              paddingBottom: 0,
              paddingTop: 0,
            },
            '& .MuiCardActions-root': {
              padding: 4,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => {
          const rarity = ownerState.rarity as string | undefined;
          const isRareCard = isRare(rarity);
          const glow = JSON.parse(JSON.stringify(GLOWS.card));
          if (isRareCard) {
            glow['&:hover'].backgroundImage = `linear-gradient(${
              ELEVATIONS_SOLID[16]
            }, ${ELEVATIONS_SOLID[16]}), ${PALETTE.rarityBackground[rarity!]}`;
            glow['&:hover'].boxShadow = `0px 0px 24.444px ${
              PALETTE.rarityHover[rarity!]
            }, 0px 0px 13.968px ${
              PALETTE.rarityHover[rarity!]
            }, 0px 0px 8.148px ${
              PALETTE.rarityHover[rarity!]
            }, 0px 0px 4.074px ${
              PALETTE.rarityHover[rarity!]
            }, 0px 0px 1.164px ${
              PALETTE.rarityHover[rarity!]
            }, 0px 0px 0.582px ${PALETTE.rarityHover[rarity!]}`;
          } else {
            glow['&:hover'].backgroundImage =
              PALETTE.background.backgroundGradient('16');
          }
          return {
            border: 'double 2px transparent',
            borderRadius: '10px',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID[8]}, ${
              ELEVATIONS_SOLID[8]
            }), ${
              isRareCard
                ? PALETTE.rarityBackground[rarity!]
                : PALETTE.primary.vertical
            }`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: SHADOWS[4],
            backdropFilter: BACKDROP_FILTERS[4],
            ...glow,
          };
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: ZINDEX.dialog,
          backdropFilter: BACKDROP_FILTERS[1],
          '& .MuiPaper-root': {
            borderRadius: '10px',
            ...GLOWS.dialog,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          border: 'double 2px transparent',
          borderRadius: '10px',
          backgroundImage: `linear-gradient(${PALETTE.background.elevation[8]}, 
            ${PALETTE.background.elevation[8]}), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'content-box, border-box',
          padding: '0px',
          overflowY: 'auto',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant === ('text' as any)) {
            return {
              '&::before, &::after': {
                borderColor: `${PALETTE.primary.dark}`,
              },
            };
          }
          if (ownerState.orientation === 'vertical') {
            return {
              background: PALETTE.primary.vertical,
              width: '1px',
              border: 'none',
            };
          }
          return {
            background: PALETTE.other.divider,
            height: '1px',
            border: 'none',
          };
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            background: ELEVATIONS[10],
            backdropFilter: BACKDROP_FILTERS[8],
            boxShadow: SHADOWS[2],
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          //boxShadow: 'inset 0px -1px 0px rgba(255, 255, 255, 0.12)',
          textTransform: 'uppercase',
          color: '#fff',
        },
      },
    },
    MuiSelect: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            border: 'double 1px transparent',
            borderRadius: '5x',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: SHADOWS_HARD.primaryElevation1,
            color: PALETTE.text.primary,
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
            '&:hover': {
              boxShadow: `0px 3px 0.5px ${SHADOWS_HARD_COLOR[`primary`]}`,
              transform: 'translateY(-1px)',
              filter: 'brightness(110%)',
              border: '1px solid #4DC8FF !important',
            },
          },
        },
      ],
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontStyle: 'normal',
          fontWeight: 700,
          fontSize: '13px',
          lineHeight: '18px',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: PALETTE.text.primary,
          filter: `drop-shadow(${SHADOWS_HARD.primaryElevation1})`,
        },
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            border: '1px solid rgba(255,255,255,0.26)',
            background: ELEVATIONS[4],
            filter: `drop-shadow(${SHADOWS_HARD.primaryElevation1})`,
            color: PALETTE.text.secondary,
          },
        },
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            border: '1px solid rgba(255,255,255,0.26)',
            background: '#083385',
            filter: `drop-shadow(${SHADOWS_HARD.primaryElevation1})`,
            color: PALETTE.text.secondary,
          },
        },
        {
          props: { variant: 'royalOutlined' },
          style: {
            background: 'unset',
            backgroundColor: 'unset',
            border: 'double 1px transparent',
            backgroundImage: `linear-gradient(#442f6e, #39275e), ${PALETTE.royal.verticalInverse}`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: 'none',
            filter: 'none',
          },
        },
        {
          props: { variant: 'gradientOutlined' },
          style: {
            border: 'double 1px transparent',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
          },
        },
        {
          props: { variant: 'gradientOutlinedHorizontal' },
          style: {
            border: 'double 1px transparent',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['16']}, ${ELEVATIONS_SOLID['16']}), linear-gradient(90deg, #4DC8FF 0%, #0064E9 100%)`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
          },
        },
        ...Object.keys(PALETTE.rarityBackground).map((rarity) => ({
          props: { variant: rarity },
          style: {
            background: PALETTE.rarityBackground[`${rarity}`],
          },
        })),
        ...Object.keys(PALETTE.rarityBackground).map((rarity) => ({
          props: { variant: `${rarity}Outlined` },
          style: {
            background: 'unset',
            backgroundColor: 'unset',
            border: 'double 1px transparent',
            backgroundImage: `linear-gradient(${tinyColor(
              RARITIES[`${rarity}`],
            ).darken(30)}, ${tinyColor(RARITIES[`${rarity}`]).darken(35)}), ${
              PALETTE.rarityBackground[`${rarity}`]
            }`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            boxShadow: 'none',
            filter: 'none',
          },
        })),
      ],
    },
    MuiSnackbar: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.message) {
            return {
              border: 'double 2px transparent',
              borderRadius: '5px',
              backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), linear-gradient(180deg, #4DC8FF 0%, #0064E9 100%)`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'content-box, border-box',
              '& .MuiPaper-root': {
                background: 'transparent',
              },
            };
          }
          return {
            backdropFilter: theme.glass[8],
            boxShadow: theme.shadows[2],
            borderRadius: '4px',
            '& .MuiPaper-root': {
              background: ELEVATIONS['8'],
            },
          };
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          background: ELEVATIONS['10'],
          backdropFilter: BACKDROP_FILTERS[2],
          ...GLOWS.highlight,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: () => {
          return {
            '& .MuiCircularProgress-colorPrimary': {
              color: PALETTE.primary.horizontal,
            },
          };
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            background: PALETTE.background.elevation['4'],
            height: '8px',
            borderRadius: '4px',
            overflow: 'visible',
            '& .MuiLinearProgress-barColorPrimary': {
              background: PALETTE.primary.horizontal,
              boxShadow:
                '0px 0px 5px -1px rgba(57, 156, 250, 0.8), 0px 0px 8px rgba(57, 156, 250, 0.50), 0px 0px 14px rgba(57, 156, 250, 0.40)',
              borderRadius: '4px',
              width: `${ownerState.value}% !important`,
              transform: 'translateX(0%) !important',
            },
            '& .MuiLinearProgress-barColorSecondary': {
              background: PALETTE.secondary.horizontal,
            },
            '& .MuiLinearProgress-dashed': {
              backgroundImage: `conic-gradient(${PALETTE.text.secondary} 50%, ${PALETTE.text.secondary}, transparent 0%)`,
            },
          };
        },
      },
      variants: [
        {
          props: { variant: 'buffer' },
          style: {
            backgroundColor: 'transparent !important',
          },
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.disabled) {
            return;
          }
          return {
            '& .MuiSvgIcon-root': {
              color: PALETTE.text.secondary,
            },
            background: PALETTE.primary.vertical,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          };
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            border: 'double 1px transparent',
            borderRadius: '5px',
            backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['10']}, ${ELEVATIONS_SOLID['10']}), ${PALETTE.primary.vertical}`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'content-box, border-box',
            backdropFilter: BACKDROP_FILTERS[0],
            boxShadow: SHADOWS_HARD.primaryElevation2,
            '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
              boxShadow: ownerState.hideDivider
                ? 'none'
                : 'inset -1px 0px 0px rgba(77, 198, 255, 0.5)',
            },
            '& .Mui-selected': {
              background: `linear-gradient(${ELEVATIONS_SOLID['6']}, ${ELEVATIONS_SOLID['9']})`,
              WebkitBackgroundClip: 'none',
              WebkitTextFillColor: PALETTE.primary.mid,
              backgroundClip: 'none',
            },
            '& .Mui-disabled': {
              WebkitBackgroundClip: 'none',
              WebkitTextFillColor: PALETTE.text.disabled,
              opacity: '50%',
              backgroundClip: 'none',
            },
          };
        },
      },
    },
    // If the badge is invisible for some reason, make sure to pass badgeContent={''}
    // this is because the MUI checks for visibility rely on the variant not being a custom one.
    MuiBadge: {
      styleOverrides: {
        root: () => {
          return {
            '& .MuiBadge-colorPrimary': {
              background: PALETTE.primary.horizontal,
            },
            '& .MuiBadge-colorSecondary': {
              background: PALETTE.secondary.horizontal,
            },
            '& .MuiBadge-colorError': {
              background: PALETTE.error.vertical,
            },
            '& .MuiBadge-colorWarning': {
              background: PALETTE.warning.vertical,
            },
            '& .MuiBadge-colorInfo': {
              background: PALETTE.info.vertical,
            },
            '& .MuiBadge-colorSuccess': {
              background: PALETTE.success.vertical,
            },
          };
        },
      },
      variants: [
        {
          props: { variant: 'standard' },
          style: {
            '& .MuiBadge-badge': {
              minWidth: '18px',
              width: '18px',
              height: '20px',
              borderRadius: '64px',
            },
          },
        },
        {
          props: { variant: 'online' },
          style: {
            '& .MuiBadge-badge': {
              border: '2px solid #fff',
              width: '14px',
              minWidth: '14px',
              height: '14px',
              borderRadius: '50%',
              padding: 0,
            },
          },
        },
        {
          props: { variant: 'onlineLarge' },
          style: {
            '& .MuiBadge-badge': {
              border: '3px solid #fff',
              width: '20px',
              minWidth: '20px',
              height: '20px',
              borderRadius: '50%',
              padding: 0,
            },
          },
        },
      ],
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontWeight: 600,
          fontSize: 14,
          lineHeight: 1.4,
          borderRadius: '5px',
          border: '1px solid #399CFA33',
          background:
            'linear-gradient(0deg, rgba(0, 100, 233, 0.15), rgba(0, 100, 233, 0.15)), rgba(5, 29, 51, 0.6)',
          boxShadow: SHADOWS_HARD.primaryElevation1,
          backdropFilter: BACKDROP_FILTERS[17],
          '& .MuiTooltip-arrow': {
            color: 'rgb(7,50,201)',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const color = ownerState.color || 'primary';
          return {
            '& .MuiSlider-rail': {
              background: PALETTE[`${color}`].horizontal,
              opacity: 0.38,
              borderRadius: '12px',
            },
            '& .MuiSlider-track': {
              boxShadow: SHADOWS_HARD[`${color}Elevation1`],
              background: PALETTE[`${color}`].horizontal,
              border: 'none',
            },
            '& .MuiSlider-thumb': {
              background: PALETTE[`${color}`].horizontal,
              boxShadow: SHADOWS_HARD[`${color}Elevation1`],
            },
            '& .MuiSlider-mark': {
              background: PALETTE[`${color}`].horizontal,
            },
          };
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: ELEVATIONS[4],
          backdropFilter: BACKDROP_FILTERS[1],
          borderRadius: '5px 5px 0px 0px',
        },
      },
      variants: [
        {
          props: { variant: 'filter' },
          style: {
            '& .MuiAccordionSummary-root': {
              borderRadius: '5px 5px 0px 0px',
              //background: 'rgba(0, 101, 232, 0.14)',
              borderBottom: '1px solid transparent',
              borderImage: PALETTE.other.divider,
              borderImageSlice: 1,
            },
            '& .MuiAccordionDetails-root': {
              //background: 'rgba(0, 101, 232, 0.14)',
              borderBottom: '1px solid transparent',
              borderImage: PALETTE.other.divider,
              borderImageSlice: 1,
            },
          },
        },
        {
          props: { variant: 'listing' },
          style: {
            '& .MuiAccordionSummary-root': {
              background: 'rgba(69, 198, 255, 0.16)',
              borderBottom: '1px solid transparent',
              borderImage: PALETTE.other.divider,
              borderImageSlice: 1,
            },
            '& .MuiAccordionDetails-root': {
              background: 'rgba(69, 198, 255, 0.16)',
              borderBottom: '1px solid transparent',
              borderImage: PALETTE.other.divider,
              borderImageSlice: 1,
            },
          },
        },
        {
          props: { variant: 'listings' },
          style: {
            background: 'transparent',
            '& .MuiAccordionDetails-root': {
              background: 'transparent',
            },
          },
        },
      ],
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ ownerState }) => {
          return {
            boxShadow: SHADOWS[1],
            backdropFilter: BACKDROP_FILTERS[1],
            padding: '0 16px',
            borderBottom: ownerState.expanded
              ? 'none'
              : '1px solid transparent',
            borderImage: PALETTE.other.divider,
            borderImageSlice: 1,
          };
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          '& a.MuiTypography-root': {
            ...TYPOGRAPHY.h6,
            fontWeight: 400,
            background: PALETTE.primary.vertical,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            textDecorationColor: PALETTE.primary.dark,
            // textDecoration: 'none',
            // color: PALETTE.primary.main,
            // textDecoration: 'underline',
            // fontSize: 22,
            // borderImage: PALETTE.other.divider,
            // borderBottom: '1px solid transparent',
            // borderImageSlice: 1,
          },
          '& li:last-of-type': {
            // marginBottom: 2,
            '& a.MuiTypography-root': {
              // fontFamily: 'Rubik',
              background: 'none',
              // webkitBackgroundClip: 'unset',
              // webkitTextFillColor: 'unset',
              // backgroundClip: 'unset',
              // textFillColor: 'unset',
              // fontWeight: 600,
              textFillColor: PALETTE.text.secondary,
              // letterSpacing: 0.15,
              textDecoration: 'none',
              // fontSize: 24,
              // marginBottom: 0,
              // '&:hover': { border: 'none' },
            },
          },
        },
        separator: {
          ...TYPOGRAPHY.h6,
          fontWeight: 400,
          background: PALETTE.primary.vertical,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          //background: 'rgba(77, 200, 255, 0.05)',
          opacity: '80%',
          background: PALETTE.primary.vertical,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          textFillColor: 'transparent',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid transparent',
          borderImage: PALETTE.primary.horizontal,
          borderImageSlice: 1,
          '& .Mui-selected': {
            opacity: '100%',
            background: PALETTE.background.elevation['10'],
            WebkitBackgroundClip: 'unset',
            WebkitTextFillColor: 'unset',
            backgroundClip: 'unset',
            textFillColor: 'unset',
            // 'linear-gradient(0deg, #0064E9 -33%, #4DC8FF 90%)'
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          background: 'transparent',
          padding: '8px 16px',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: PALETTE.action.opaque,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const severityColor =
            theme.palette[`${ownerState.severity || 'primary'}`];
          const backgroundLight = `${severityColor.light}26`; // 20% opacity
          const backgroundDark = `${severityColor.dark}26`; // 20% opacity
          return {
            textTransform: 'uppercase',
            borderRadius: '10px',
            // boxShadow: `${
            //   SHADOWS_HARD[`${ownerState.severity}Elevation2`]
            // } !important`,
            '.MuiAlert-icon': {
              padding: 0,
              marginTop: 'auto',
              marginBottom: 'auto',
            },
            backgroundImage: `linear-gradient(${backgroundLight}, ${backgroundDark})`,
          };
        },
      },
    },
    MuiTextField: {
      variants: [
        ...['primary', 'secondary', 'warning', 'error', 'success', 'info'].map(
          (color) => ({
            props: {
              variant: 'outlined' as any,
              color: color as any,
              disabled: false,
            },
            style: {
              // '.MuiInputLabel-root': {
              //   top: '-8px',
              //   left: '-6px',
              //   color: PALETTE[color as string].main,
              // },
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: `${PALETTE[color as string].main} !important`,
                borderWidth: '1.25px',
              },
              '.MuiOutlinedInput-root': {
                // '&:hover': {
                //   border: 'double 1px transparent',
                //   borderRadius: '5px',
                //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${
                //     ELEVATIONS_SOLID['8']
                //   }), ${PALETTE[color as string].horizontal}`,
                //   backgroundClip: 'content-box',
                //   boxShadow: SHADOWS_HARD[`${color}Elevation1`],
                //   backdropFilter: BACKDROP_FILTERS[0],
                // },
                // '&:focus': {
                //   border: 'double 1px transparent',
                //   borderRadius: '5px',
                //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${
                //     ELEVATIONS_SOLID['8']
                //   }), ${PALETTE[color as string].horizontal}`,
                //   backgroundClip: 'content-box',
                //   boxShadow: SHADOWS_HARD[`${color}Elevation1`],
                //   backdropFilter: BACKDROP_FILTERS[0],
                // },
                // '.Mui-focused': {
                //   border: 'double 1px transparent',
                //   borderRadius: '5px',
                //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${
                //     ELEVATIONS_SOLID['8']
                //   }), ${PALETTE[color as string].horizontal}`,
                //   backgroundClip: 'content-box',
                //   boxShadow: SHADOWS_HARD[`${color}Elevation1`],
                //   backdropFilter: BACKDROP_FILTERS[0],
                // },
                borderRadius: '5px',
                boxShadow: SHADOWS_HARD[`${color}Elevation1`],
                // backdropFilter: BACKDROP_FILTERS[0],
              },
            },
          }),
        ),
        {
          props: {
            variant: 'outlined' as any,
            disabled: false,
            error: true,
          },
          style: {
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: `${PALETTE.error.main} !important`,
              borderWidth: '1.25px',
            },
            '.MuiOutlinedInput-root': {
              // '&:hover': {
              //   border: 'double 1px transparent',
              //   borderRadius: '5px',
              //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), ${PALETTE.error.horizontal}`,
              //   backgroundOrigin: 'border-box',
              //   backgroundClip: 'content-box, border-box',
              //   boxShadow: SHADOWS_HARD.errorElevation1,
              //   backdropFilter: BACKDROP_FILTERS[0],
              // },
              // '&:focus': {
              //   border: 'double 1px transparent',
              //   borderRadius: '5px',
              //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), ${PALETTE.error.horizontal}`,
              //   backgroundOrigin: 'border-box',
              //   backgroundClip: 'content-box, border-box',
              //   boxShadow: SHADOWS_HARD.errorElevation1,
              //   backdropFilter: BACKDROP_FILTERS[0],
              // },
              // '.Mui-focused': {
              //   border: 'double 1px transparent',
              //   borderRadius: '5px',
              //   backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${ELEVATIONS_SOLID['8']}), ${PALETTE.error.horizontal}`,
              //   backgroundOrigin: 'border-box',
              //   backgroundClip: 'content-box, border-box',
              //   boxShadow: SHADOWS_HARD.errorElevation1,
              //   backdropFilter: BACKDROP_FILTERS[0],
              // },
              borderRadius: '5px',
              boxShadow: SHADOWS_HARD.errorElevation1,
              // backdropFilter: BACKDROP_FILTERS[0],
            },
          },
        },
        // {
        //   props: {
        //     variant: 'outlined' as any,
        //     disabled: false,
        //     multiline: true,
        //   },
        //   style: {
        //     '.MuiOutlinedInput-root': {
        //       padding: 0,
        //     },
        //     '.MuiOutlinedInput-input': {
        //       margin: '16.5px 14px 16.5px 0px',
        //     },
        //     '.MuiInputAdornment-root': {
        //       margin: '16.5px 0px 16.5px 14px',
        //     },
        //   },
        // },
        // {
        //   props: {
        //     variant: 'outlined' as any,
        //     disabled: false,
        //   },
        //   style: {
        //     '.MuiOutlinedInput-notchedOutline': {
        //       color: 'transparent',
        //     },
        //     '.MuiOutlinedInput-root': {
        //       padding: 0,
        //     },
        //     '.MuiInputAdornment-root': {
        //       margin: '0px 8px 0px 8px',
        //     },
        //   },
        // },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          const background =
            ownerState.variant === 'contained' && !ownerState.disabled
              ? ownerState.rarity &&
                Object.keys(PALETTE.rarity).includes(
                  ownerState.rarity as string,
                )
                ? PALETTE.rarityBackground[ownerState.rarity as string]
                : ownerState.color
                ? PALETTE[ownerState.color].vertical
                : PALETTE.primary.vertical
              : undefined;
          return {
            boxShadow: SHADOWS_HARD.primaryElevation2,
            background,
          };
        },
      },
      variants: [
        {
          props: { variant: 'contained', disabled: false },
          style: {
            '&:hover': {
              filter: 'brightness(110%)',
            },
          },
        },
        ...[
          'primary',
          'secondary',
          'warning',
          'error',
          'success',
          'info',
          'royal',
        ].map((color) => ({
          props: {
            variant: 'contained' as any,
            color: color as any,
            disabled: false,
          },
          style: {
            background: PALETTE[`${color}`].vertical,
            boxShadow: SHADOWS_HARD[`${color}Elevation2`],
            '&:hover': {
              filter: 'brightness(110%)',
              boxShadow: SHADOWS_HARD[`${color}Elevation2`],
            },
          },
        })),
        ...['primary', 'secondary', 'warning', 'error', 'success', 'info'].map(
          (color) => ({
            props: {
              variant: 'outlined' as any,
              color: color as any,
              disabled: false,
            },
            style: {
              lineHeight: 0,
              border: 'double 1px transparent',
              borderRadius: '5px',
              backgroundImage: `linear-gradient(${ELEVATIONS_SOLID['8']}, ${
                ELEVATIONS_SOLID['8']
              }), ${PALETTE[color as string].vertical}`,
              backgroundOrigin: 'border-box',
              backgroundClip: 'content-box, border-box',
              boxShadow: SHADOWS_HARD[`${color}Elevation1`],
              backdropFilter: BACKDROP_FILTERS[0],
              padding: '0px',
              '&:hover': {
                boxShadow: `0px 3px 0.5px ${SHADOWS_HARD_COLOR[`${color}`]}`,
                transform: 'translateY(-1px)',
              },
            },
          }),
        ),
        {
          props: { size: 'large' },
          style: {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '24px',
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
          },
        },
        {
          props: { size: 'small' },
          style: {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '13px',
            lineHeight: '22px',
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
          },
        },
      ],
    },
    // MuiAccordionSummary: {
    //   styleOverrides: {
    //     root: {
    //       //background: PALETTE.background.elevation[16],
    //       padding: '0px 16px',
    //       width: 'min(1804px, 100%)',
    //       marginInline: 'auto',
    //       borderRadius: '4px',
    //       '& .MuiAccordionSummary-expandIconWrapper': {
    //         paddingRight: '12px',
    //         paddingLeft: '0',
    //       },
    //       '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    //         paddingRight: '0',
    //         paddingLeft: '12px',
    //       },
    //       maxHeight: '56px',
    //     },
    //   },
    // },
    // MuiAccordion: {
    //   styleOverrides: {
    //     root: {
    //       background: 'transparent',
    //       boxShadow: 'unset',
    //       borderRadius: '4px',
    //     },
    //   },
    // },
  },
});

export type SxPropsTheme = SxProps<typeof theme>;

export default theme;
